import React, { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { AddonId, AddonProvider, getProviders, getProvider, getCategoryMeta } from './addonProviders';
import { runAddonSync, verifyAddonConnection, SyncOrg } from '../services/addonSync';
import { saveAddonCredentials } from '../services/addonsApi';
import { CustomToast } from './customToast';

/** Org add-on entry shape (mirrors the Organization.addons map in Firestore). */
export interface OrgAddon {
  wanted: boolean;
  status: 'default' | 'connected' | 'off';
  /** Set true only after a successful "Save & verify" test. The comparison engine
   *  refuses to run until this is true. Cleared whenever credentials change. */
  verified?: boolean;
  verifiedAt?: any;
  lastTest?: { ok: boolean; at: any; message?: string; sampleCount?: number };
  apiUrl?: string;
  provider?: string;
  apiKey?: string;
  extraFields?: Record<string, string>;
}

/** Human-readable status line, reused on the org settings page. */
export function addonStatusLabel(status: OrgAddon['status'] | undefined, verified?: boolean): string {
  if (status === 'off') return 'Off';
  if (status === 'connected' && verified) return 'Connected & verified — comparisons active';
  if (status === 'connected') return 'Connected — run the test to start comparisons';
  return 'On — not connected (app runs in default mode)';
}

interface AddonConnectorProps {
  orgCode: string;
  addonKey: AddonId;
  addon: OrgAddon;
  /** Called after a successful save so the parent can patch its local copy. */
  onSaved?: (key: AddonId, patch: Partial<OrgAddon>) => void;
}

/**
 * Self-contained connector card for a single add-on: pick provider, fill the
 * provider's fields, test reachability, save credentials to Firestore. Used both
 * inline on the org settings page and inside the Dashboard integrations modal.
 */
export default function AddonConnector({ orgCode, addonKey, addon, onSaved }: AddonConnectorProps) {
  const categoryMeta = getCategoryMeta(addonKey);
  const providers = getProviders(addonKey);

  const [providerDraft, setProviderDraft] = useState<string>(addon.provider || '');
  const [urlDraft, setUrlDraft] = useState<string>(addon.apiUrl || '');
  const [keyDraft, setKeyDraft] = useState<string>(addon.apiKey || '');
  const [extraFields, setExtraFields] = useState<Record<string, string>>(addon.extraFields || {});
  const [busy, setBusy] = useState<'idle' | 'saving' | 'verifying'>('idle');
  const [lastResult, setLastResult] = useState<{ ok: boolean; message: string } | null>(
    addon.lastTest ? { ok: addon.lastTest.ok, message: addon.lastTest.message || '' } : null
  );

  const selectedProvider: AddonProvider | undefined = providerDraft ? getProvider(addonKey, providerDraft) : undefined;

  /**
   * Save credentials, then immediately run the verification test. The add-on is only
   * marked connected+verified (which is what lets the comparison engine run) if the test
   * confirms data is actually pulling. A failed test saves the credentials but leaves the
   * add-on unverified, so nothing ever syncs against a broken connection.
   */
  async function handleSaveAndVerify() {
    if (!orgCode) return;
    const ref = doc(db, 'organizations', orgCode);

    // 1. Persist credentials via the BACKEND (server-only addonSecrets subcollection),
    //    never into the world-readable org doc (C5). The backend also resets
    //    provider/status/verified on the org doc, so the add-on must re-verify.
    setBusy('saving');
    const saved = await saveAddonCredentials(orgCode, addonKey, {
      provider: providerDraft, apiUrl: urlDraft, apiKey: keyDraft || '', extraFields: extraFields || {},
    });
    if (!saved.ok) {
      CustomToast.show(saved.message || 'Failed to save credentials. Please try again.', 'error');
      setBusy('idle');
      return;
    }

    // 2. Run the verification test against the just-saved credentials.
    setBusy('verifying');
    const trialOrg: SyncOrg = {
      code: orgCode,
      addons: { [addonKey]: { wanted: true, status: 'connected', verified: true, provider: providerDraft, apiUrl: urlDraft, apiKey: keyDraft, extraFields } },
    };
    let result;
    try {
      result = await verifyAddonConnection(trialOrg, addonKey);
    } catch {
      result = { ok: false, sampleCount: 0, message: 'Verification failed unexpectedly. Please try again.' };
    }

    const at = new Date();
    try {
      await updateDoc(ref, {
        [`addons.${addonKey}.status`]: result.ok ? 'connected' : 'default',
        [`addons.${addonKey}.verified`]: result.ok,
        [`addons.${addonKey}.verifiedAt`]: result.ok ? at : null,
        [`addons.${addonKey}.lastTest`]: { ok: result.ok, at, message: result.message, sampleCount: result.sampleCount },
        updatedAt: new Date(),
      });
    } catch { /* non-fatal — the test result is advisory */ }

    onSaved?.(addonKey, {
      provider: providerDraft, apiUrl: urlDraft, apiKey: keyDraft, extraFields,
      status: result.ok ? 'connected' : 'default', verified: result.ok,
      verifiedAt: result.ok ? at : undefined,
      lastTest: { ok: result.ok, at, message: result.message, sampleCount: result.sampleCount },
    });
    setLastResult({ ok: result.ok, message: result.message });
    CustomToast.show(result.message, result.ok ? 'success' : 'error');

    // 3. On success, kick off the first comparison run right away.
    if (result.ok) runAddonSync(trialOrg, addonKey).catch(() => { /* mock/offline — ignore */ });
    setBusy('idle');
  }

  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 16px', marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 20 }}>{categoryMeta?.icon}</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 13.5 }}>{categoryMeta?.label || addonKey}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>{categoryMeta?.description}</div>
          </div>
        </div>
        {addon.status === 'connected'
          ? <span style={{ fontSize: 11, fontWeight: 700, color: '#16a34a', background: '#dcfce7', borderRadius: 8, padding: '2px 9px', flexShrink: 0 }}>● Connected</span>
          : <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', background: '#f1f5f9', borderRadius: 8, padding: '2px 9px', flexShrink: 0 }}>Not connected</span>}
      </div>

      <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 6 }}>Choose provider</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
        {providers.map(provider => (
          <button
            key={provider.id}
            onClick={() => setProviderDraft(provider.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '5px 10px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600,
              border: providerDraft === provider.id ? `2px solid ${provider.accentColor}` : '1.5px solid #e2e8f0',
              background: providerDraft === provider.id ? `${provider.accentColor}12` : '#f8fafc',
              color: providerDraft === provider.id ? provider.accentColor : '#475569',
              transition: 'all 0.12s',
            }}
          >
            <span>{provider.emoji}</span>
            <span>{provider.shortName}</span>
          </button>
        ))}
      </div>

      {selectedProvider && (
        <div>
          {selectedProvider.notes && (
            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#92400e', marginBottom: 10 }}>
              💡 {selectedProvider.notes}
            </div>
          )}
          {selectedProvider.fields.map(field => (
            <div key={field.key} style={{ marginBottom: 8 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b', display: 'block', marginBottom: 3 }}>
                {field.label}{field.required && <span style={{ color: '#dc2626' }}> *</span>}
              </label>
              <input
                type={field.type}
                placeholder={field.placeholder}
                value={
                  field.key === 'apiUrl' ? urlDraft :
                  field.key === 'apiKey' ? keyDraft :
                  (extraFields[field.key] || '')
                }
                onChange={(e) => {
                  if (field.key === 'apiUrl') setUrlDraft(e.target.value);
                  else if (field.key === 'apiKey') setKeyDraft(e.target.value);
                  else setExtraFields(prev => ({ ...prev, [field.key]: e.target.value }));
                }}
                style={{ width: '100%', padding: '8px 11px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' }}
              />
              {field.hint && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>{field.hint}</div>}
            </div>
          ))}

          <button
            disabled={busy !== 'idle' || !urlDraft}
            style={{ width: '100%', padding: '9px', background: (busy !== 'idle' || !urlDraft) ? '#94a3b8' : '#3b5bdb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12.5, fontWeight: 700, cursor: (busy !== 'idle' || !urlDraft) ? 'not-allowed' : 'pointer', fontFamily: 'inherit', marginTop: 10 }}
            onClick={handleSaveAndVerify}
          >
            {busy === 'saving' ? 'Saving…' : busy === 'verifying' ? 'Running test — pulling data…' : (addon.verified ? 'Re-run test' : 'Save & verify connection')}
          </button>
          {lastResult && (
            <div style={{ marginTop: 8, fontSize: 12, borderRadius: 8, padding: '8px 11px', background: lastResult.ok ? '#dcfce7' : '#fee2e2', color: lastResult.ok ? '#166534' : '#b91c1c' }}>
              {lastResult.ok ? '✓ ' : '✗ '}{lastResult.message}
            </div>
          )}
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
            Comparisons only start once the test passes — until then the app runs in its normal mode, untouched.
          </div>
        </div>
      )}

      {!selectedProvider && addon.apiUrl && (
        <div style={{ fontSize: 12, color: '#64748b', background: '#f8fafc', borderRadius: 8, padding: '8px 12px' }}>
          Previously connected to: <b>{addon.provider || 'unknown provider'}</b> · <code style={{ fontSize: 11 }}>{addon.apiUrl}</code>
        </div>
      )}
    </div>
  );
}
